import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { 
  ArrowRightLeft,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Calendar,
  Loader2,
  RefreshCw,
  Download,
  Filter,
  Search,
  AlertCircle
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { format, subMonths, startOfMonth, endOfMonth } from 'date-fns';

// Currency configs
const CURRENCIES = {
  USD: { symbol: '$', name: 'US Dollar', flag: '🇺🇸' },
  EUR: { symbol: '€', name: 'Euro', flag: '🇪🇺' },
  GBP: { symbol: '£', name: 'British Pound', flag: '🇬🇧' },
  NGN: { symbol: '₦', name: 'Nigerian Naira', flag: '🇳🇬' },
  GHS: { symbol: '₵', name: 'Ghanaian Cedi', flag: '🇬🇭' },
};

export const FXGainLossReport = ({ baseCurrency = 'NGN', dateRange, onRefresh }) => {
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [filterCurrency, setFilterCurrency] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  // Calculate date range
  // ReportsPage passes {start, end} (getDateRange); older callers used {startDate, endDate}. Accept both so the
  // Period filter actually drives the query (it used to silently fall back to "last month → this month").
  const startDate = dateRange?.start || dateRange?.startDate || format(startOfMonth(subMonths(new Date(), 1)), 'yyyy-MM-dd');
  const endDate = dateRange?.end || dateRange?.endDate || format(endOfMonth(new Date()), 'yyyy-MM-dd');
  
  // Fetch FX Gain/Loss data
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      // UNIFIED: Fetch FX Gain/Loss from unified endpoint (Single Source of Truth - journals_unified)
      const response = await axios.get(`${API}/unified-reports/fx-gain-loss`, {
        params: {
          start_date: startDate,
          end_date: endDate
        }
      });
      
      const data = response.data || {};
      
      // Process transactions from unified response
      const fxGains = (data.fx_gains?.transactions || []).map(t => ({
        id: `gain-${t.reference}`,
        date: t.date,
        reference: t.reference,
        description: t.description,
        type: 'gain',
        amount: t.amount,
        currency: t.currency || baseCurrency,
        source_type: 'fx_gain'
      }));
      
      const fxLosses = (data.fx_losses?.transactions || []).map(t => ({
        id: `loss-${t.reference}`,
        date: t.date,
        reference: t.reference,
        description: t.description,
        type: 'loss',
        amount: t.amount,
        currency: t.currency || baseCurrency,
        source_type: 'fx_loss'
      }));
      
      const fxTransactions = [...fxGains, ...fxLosses].sort((a, b) => 
        new Date(b.date) - new Date(a.date)
      );
      
      setTransactions(fxTransactions);
      
      // Use summary from unified response
      const totalGains = data.fx_gains?.total || 0;
      const totalLosses = data.fx_losses?.total || 0;
      
      // Group by currency
      const byCurrency = {};
      fxTransactions.forEach(t => {
        const curr = t.currency || 'Unknown';
        if (!byCurrency[curr]) {
          byCurrency[curr] = { gains: 0, losses: 0, count: 0 };
        }
        if (t.type === 'gain') {
          byCurrency[curr].gains += t.amount;
        } else {
          byCurrency[curr].losses += Math.abs(t.amount);
        }
        byCurrency[curr].count++;
      });
      
      setReportData({
        totalGains,
        totalLosses,
        netGainLoss: data.net_fx_impact || (totalGains - totalLosses),
        transactionCount: fxTransactions.length,
        byCurrency,
        realizedGains: data.fx_gains?.realized || 0,
        unrealizedGains: data.fx_gains?.unrealized || 0,
        realizedLosses: data.fx_losses?.realized || 0,
        unrealizedLosses: data.fx_losses?.unrealized || 0,
        glReconciliation: data.gl_reconciliation || null,
        source: 'journals_unified'
      });
      
    } catch (error) {
      console.error('Failed to fetch FX data:', error);
      toast.error('Failed to load FX Gain/Loss report');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);
  
  // Extract currency from description
  const extractCurrency = (description) => {
    const match = description?.match(/\(([A-Z]{3})\)/);
    return match ? match[1] : null;
  };
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Filter transactions
  const filteredTransactions = transactions.filter(t => {
    if (filterCurrency !== 'all' && t.currency !== filterCurrency) return false;
    if (filterType !== 'all' && t.type !== filterType) return false;
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        t.reference?.toLowerCase().includes(search) ||
        t.description?.toLowerCase().includes(search)
      );
    }
    return true;
  });
  
  // Format currency
  const formatCurrency = (v) => `${CURRENCIES[baseCurrency]?.symbol || baseCurrency + ' '}${Math.abs(Number(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatAmount = (amount, showSign = true) => {
    const formatted = Math.abs(amount).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
    if (showSign && amount !== 0) {
      return amount >= 0 ? `+${formatted}` : `-${formatted}`;
    }
    return formatted;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100/50 dark:from-emerald-900/20 dark:to-emerald-800/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-700 dark:text-emerald-400">Total FX Gains</p>
                <p className="text-2xl font-bold text-emerald-900 dark:text-emerald-100">
                  {(CURRENCIES[baseCurrency]?.symbol || baseCurrency + ' ')}{formatAmount(reportData?.totalGains || 0, false)}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-red-50 to-red-100/50 dark:from-red-900/20 dark:to-red-800/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-700 dark:text-red-400">Total FX Losses</p>
                <p className="text-2xl font-bold text-red-900 dark:text-red-100">
                  {(CURRENCIES[baseCurrency]?.symbol || baseCurrency + ' ')}{formatAmount(reportData?.totalLosses || 0, false)}
                </p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card className={`bg-gradient-to-br ${
          (reportData?.netGainLoss || 0) >= 0 
            ? 'from-blue-50 to-blue-100/50 dark:from-blue-900/20 dark:to-blue-800/10'
            : 'from-amber-50 to-amber-100/50 dark:from-amber-900/20 dark:to-amber-800/10'
        }`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm ${
                  (reportData?.netGainLoss || 0) >= 0 
                    ? 'text-blue-700 dark:text-blue-400'
                    : 'text-amber-700 dark:text-amber-400'
                }`}>Net FX Impact</p>
                <p className={`text-2xl font-bold ${
                  (reportData?.netGainLoss || 0) >= 0 
                    ? 'text-blue-900 dark:text-blue-100'
                    : 'text-amber-900 dark:text-amber-100'
                }`}>
                  {(CURRENCIES[baseCurrency]?.symbol || baseCurrency + ' ')}{formatAmount(reportData?.netGainLoss || 0)}
                </p>
              </div>
              <ArrowRightLeft className={`w-8 h-8 ${
                (reportData?.netGainLoss || 0) >= 0 ? 'text-blue-500' : 'text-amber-500'
              }`} />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800/20 dark:to-slate-700/10">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-700 dark:text-slate-400">Transactions</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-slate-100">
                  {reportData?.transactionCount || 0}
                </p>
              </div>
              <DollarSign className="w-8 h-8 text-slate-500" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Realized vs unrealized + GL tie-out — the report IS the movement on the FX ledgers for the period */}
      {reportData?.glReconciliation && (
        <Card data-testid="fx-gl-reconciliation">
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center justify-between">
              <span>Ties to the FX ledgers · {startDate} → {endDate}</span>
              <Badge className={reportData.glReconciliation.ties ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'} data-testid="fx-gl-ties-badge">
                {reportData.glReconciliation.ties ? 'Reconciled to GL' : 'Does not tie'}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              {reportData.glReconciliation.accounts.map((a) => (
                <div key={a.account_code} className="rounded-lg border p-3" data-testid={`fx-gl-${a.account_code}`}>
                  <p className="text-xs text-slate-500 font-mono">{a.account_code} · {a.realized ? 'Realized' : 'Unrealized'}</p>
                  <p className="font-medium truncate">{a.account_name}</p>
                  <p className={`text-lg font-semibold ${a.kind === 'gain' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {a.kind === 'gain' ? '+' : '-'}{formatCurrency(a.net)}
                  </p>
                  <p className="text-[11px] text-slate-500">Dr {formatCurrency(a.debit)} · Cr {formatCurrency(a.credit)}</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-slate-500 mt-3">
              Realized: gains {formatCurrency(reportData.realizedGains)} / losses {formatCurrency(reportData.realizedLosses)} ·
              Unrealized (month-end revaluation of monetary items — AR, AP, loans, bank, deposits/bonds; equities, crypto and property are not revalued):
              gains {formatCurrency(reportData.unrealizedGains)} / losses {formatCurrency(reportData.unrealizedLosses)}
            </p>
            {reportData.glReconciliation.unusual_source_count > 0 && (
              <p className="text-xs text-amber-700 mt-2" data-testid="fx-unusual-source-warning">
                {reportData.glReconciliation.unusual_source_count} posting(s) on an FX ledger did not come from an FX process (e.g. an amortisation or bill coded to 6900).
                Fix the source document's expense account — the amount will leave this report automatically.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Currency Breakdown */}
      {reportData?.byCurrency && Object.keys(reportData.byCurrency).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">FX Impact by Currency</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {Object.entries(reportData.byCurrency).map(([currency, data]) => (
                <div key={currency} className="p-3 bg-slate-50 dark:bg-slate-800/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-lg">{CURRENCIES[currency]?.flag || '🌍'}</span>
                    <span className="font-medium">{currency}</span>
                    <Badge variant="secondary" className="text-xs">{data.count}</Badge>
                  </div>
                  <div className="space-y-1 text-sm">
                    <div className="flex justify-between">
                      <span className="text-emerald-600">Gains:</span>
                      <span className="font-medium text-emerald-600">+{formatAmount(data.gains, false)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Losses:</span>
                      <span className="font-medium text-red-600">-{formatAmount(data.losses, false)}</span>
                    </div>
                    <div className="flex justify-between border-t pt-1 mt-1">
                      <span>Net:</span>
                      <span className={`font-bold ${data.gains - data.losses >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {formatAmount(data.gains - data.losses)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
      
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4" data-pdf-hide="">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input
            placeholder="Search transactions..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <Select value={filterCurrency} onValueChange={setFilterCurrency}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="Currency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Currencies</SelectItem>
            {Object.entries(CURRENCIES).map(([code, info]) => (
              <SelectItem key={code} value={code}>
                {info.flag} {code}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger className="w-32">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="gain">Gains Only</SelectItem>
            <SelectItem value="loss">Losses Only</SelectItem>
          </SelectContent>
        </Select>
        
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-1">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>
      
      {/* Transactions Table */}
      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50 dark:bg-slate-800/50">
                <TableHead>Date</TableHead>
                <TableHead>Reference</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Amount ({baseCurrency})</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTransactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-slate-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                    No FX gain/loss transactions found for this period
                  </TableCell>
                </TableRow>
              ) : (
                filteredTransactions.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-slate-400" />
                        {new Date(t.date).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{t.reference}</TableCell>
                    <TableCell className="max-w-xs truncate">{t.description}</TableCell>
                    <TableCell>
                      {t.currency ? (
                        <Badge variant="outline">
                          {CURRENCIES[t.currency]?.flag || '🌍'} {t.currency}
                        </Badge>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={t.type === 'gain' 
                        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
                        : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                      }>
                        {t.type === 'gain' ? (
                          <><TrendingUp className="w-3 h-3 mr-1" /> Gain</>
                        ) : (
                          <><TrendingDown className="w-3 h-3 mr-1" /> Loss</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className={`text-right font-mono font-semibold ${
                      t.type === 'gain' ? 'text-emerald-600' : 'text-red-600'
                    }`}>
                      {t.type === 'gain' ? '+' : '-'}{(CURRENCIES[baseCurrency]?.symbol || baseCurrency + ' ')}{formatAmount(Math.abs(t.amount), false)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default FXGainLossReport;
